import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

interface ChatbotData {
    id: string;
    name: string;
    description: string;
    status: 'draft' | 'training' | 'ready' | 'live';
    primaryUseCase: string;
    personality: {
        tone: string;
        formality: string;
        responseLength: string;
    };
    knowledge: string[];
    appearance: {
        theme: string;
        primaryColor: string;
        position: string;
    };
}

const ChatbotSettings: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [activeTab, setActiveTab] = useState('details');
    const [chatbot, setChatbot] = useState<ChatbotData | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Mock-Daten laden
    useEffect(() => {
        // Hier würdest du normalerweise eine API-Anfrage senden
        setTimeout(() => {
            setChatbot({
                id: id || '1',
                name: 'Kundenservice Bot',
                description: 'Hilft Kunden bei häufigen Fragen und Problemen',
                status: 'live',
                primaryUseCase: 'customer-service',
                personality: {
                    tone: 'friendly',
                    formality: 'casual',
                    responseLength: 'concise',
                },
                knowledge: [
                    'https://example.com/faq',
                    'https://example.com/returns-policy'
                ],
                appearance: {
                    theme: 'light',
                    primaryColor: '#3b82f6',
                    position: 'right',
                },
            });
            setIsLoading(false);
        }, 500);
    }, [id]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        if (!chatbot) return;

        const { name, value } = e.target;

        // Für verschachtelte Eigenschaften wie personality.tone
        if (name.includes('.')) {
            const [category, field] = name.split('.');
            setChatbot({
                ...chatbot,
                [category]: {
                    ...chatbot[category as keyof ChatbotData] as any,
                    [field]: value
                }
            });
        } else {
            setChatbot({
                ...chatbot,
                [name]: value
            });
        }
    };

    const handleSave = () => {
        console.log('Saving chatbot settings:', chatbot);
        // Hier würdest du die Daten an eine API senden
        alert('Einstellungen gespeichert!');
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (!chatbot) {
        return (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                <p>Chatbot nicht gefunden. Bitte versuche es erneut oder erstelle einen neuen Chatbot.</p>
                <Link to="/" className="text-blue-500 hover:underline mt-2 inline-block">
                    Zurück zur Übersicht
                </Link>
            </div>
        );
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">{chatbot.name}</h1>
                    <p className="text-gray-600">{chatbot.description}</p>
                </div>
                <div className="flex space-x-2">
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                        Änderungen speichern
                    </button>
                    <Link
                        to={`/preview/${chatbot.id}`}
                        className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                    >
                        Vorschau
                    </Link>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                    {['details', 'personality', 'knowledge', 'appearance', 'deploy'].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                                activeTab === tab
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Tab Content */}
            <div className="bg-white shadow rounded-lg p-6">
                {activeTab === 'details' && (
                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Name</label>
                            <input
                                type="text"
                                name="name"
                                value={chatbot.name}
                                onChange={handleInputChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Beschreibung</label>
                            <textarea
                                name="description"
                                rows={3}
                                value={chatbot.description}
                                onChange={handleInputChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Haupteinsatzgebiet</label>
                            <select
                                name="primaryUseCase"
                                value={chatbot.primaryUseCase}
                                onChange={handleInputChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="customer-service">Kundenservice</option>
                                <option value="sales">Vertrieb</option>
                                <option value="internal">Interne Nutzung</option>
                                <option value="other">Andere</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Status</label>
                            <div className="mt-1 flex items-center">
                <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${
                        chatbot.status === 'live' ? 'bg-green-100 text-green-800' :
                            chatbot.status === 'ready' ? 'bg-blue-100 text-blue-800' :
                                chatbot.status === 'training' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'
                    }`}
                >
                  {chatbot.status === 'live' ? 'Live' :
                      chatbot.status === 'ready' ? 'Bereit' :
                          chatbot.status === 'training' ? 'Training' :
                              'Entwurf'}
                </span>
                                <select
                                    name="status"
                                    value={chatbot.status}
                                    onChange={handleInputChange}
                                    className="ml-3 block border border-gray-300 rounded-md shadow-sm py-1 px-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="draft">Entwurf</option>
                                    <option value="training">Training</option>
                                    <option value="ready">Bereit</option>
                                    <option value="live">Live</option>
                                </select>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'personality' && (
                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Tonfall</label>
                            <select
                                name="personality.tone"
                                value={chatbot.personality.tone}
                                onChange={handleInputChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="friendly">Freundlich</option>
                                <option value="professional">Professionell</option>
                                <option value="casual">Lässig</option>
                                <option value="enthusiastic">Enthusiastisch</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Formalität</label>
                            <select
                                name="personality.formality"
                                value={chatbot.personality.formality}
                                onChange={handleInputChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="formal">Formell (Sie-Form)</option>
                                <option value="casual">Informell (Du-Form)</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Antwortlänge</label>
                            <select
                                name="personality.responseLength"
                                value={chatbot.personality.responseLength}
                                onChange={handleInputChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="concise">Kurz und knapp</option>
                                <option value="moderate">Mittellang</option>
                                <option value="detailed">Ausführlich</option>
                            </select>
                        </div>
                        <div className="bg-blue-50 p-4 rounded-md">
                            <h3 className="text-blue-800 font-semibold mb-2">Beispielantwort:</h3>
                            <p className="text-gray-700">
                                {chatbot.personality.tone === 'friendly' && "Hallo! Ich freue mich, dass du unsere Website besuchst. Wie kann ich dir heute helfen? 😊"}
                                {chatbot.personality.tone === 'professional' && "Guten Tag. Willkommen auf unserer Website. Wie kann ich Ihnen behilflich sein?"}
                                {chatbot.personality.tone === 'casual' && "Hey! Schön, dass du da bist. Was kann ich für dich tun?"}
                                {chatbot.personality.tone === 'enthusiastic' && "Wow, super dass du hier bist! Ich bin total begeistert, dir helfen zu können! Was kann ich für dich tun? 🎉"}
                            </p>
                        </div>
                    </div>
                )}

                {activeTab === 'knowledge' && (
                    <div>
                        <div className="mb-6">
                            <h2 className="text-lg font-medium text-gray-900 mb-2">Wissensquellen</h2>
                            <p className="text-gray-600 mb-4">Gib Webseiten und Dokumente an, die der Chatbot als Wissensgrundlage verwenden soll.</p>

                            <div className="space-y-2 mb-4">
                                {chatbot.knowledge.map((source, index) => (
                                    <div key={index} className="flex items-center">
                                        <input
                                            type="text"
                                            value={source}
                                            onChange={(e) => {
                                                const newKnowledge = [...chatbot.knowledge];
                                                newKnowledge[index] = e.target.value;
                                                setChatbot({
                                                    ...chatbot,
                                                    knowledge: newKnowledge
                                                });
                                            }}
                                            className="flex-1 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                        <button
                                            onClick={() => {
                                                const newKnowledge = chatbot.knowledge.filter((_, i) => i !== index);
                                                setChatbot({
                                                    ...chatbot,
                                                    knowledge: newKnowledge
                                                });
                                            }}
                                            className="ml-2 p-2 text-red-600 hover:bg-red-100 rounded"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                            </div>

                            <button
                                onClick={() => {
                                    setChatbot({
                                        ...chatbot,
                                        knowledge: [...chatbot.knowledge, '']
                                    });
                                }}
                                className="flex items-center text-blue-600 hover:text-blue-800"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                                </svg>
                                Quelle hinzufügen
                            </button>
                        </div>

                        <div className="mt-8 border-t border-gray-200 pt-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Dokumente hochladen</h3>
                            <div className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                <div className="mt-2">
                                    <label htmlFor="file-upload" className="cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                                        <span>PDF oder Dokumente hochladen</span>
                                        <input id="file-upload" name="file-upload" type="file" className="sr-only" multiple />
                                    </label>
                                </div>
                                <p className="mt-1 text-sm text-gray-500">
                                    PDF, DOCX, TXT bis 10MB
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'appearance' && (
                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Design-Theme</label>
                            <div className="mt-2 grid grid-cols-3 gap-3">
                                <label className={`border rounded-md p-3 flex items-center justify-center cursor-pointer ${chatbot.appearance.theme === 'light' ? 'bg-blue-50 border-blue-500' : 'border-gray-200'}`}>
                                    <input type="radio" name="appearance.theme" value="light" checked={chatbot.appearance.theme === 'light'} onChange={handleInputChange} className="sr-only" />
                                    <span>Hell</span>
                                </label>
                                <label className={`border rounded-md p-3 flex items-center justify-center cursor-pointer ${chatbot.appearance.theme === 'dark' ? 'bg-blue-50 border-blue-500' : 'border-gray-200'}`}>
                                    <input type="radio" name="appearance.theme" value="dark" checked={chatbot.appearance.theme === 'dark'} onChange={handleInputChange} className="sr-only" />
                                    <span>Dunkel</span>
                                </label>
                                <label className={`border rounded-md p-3 flex items-center justify-center cursor-pointer ${chatbot.appearance.theme === 'system' ? 'bg-blue-50 border-blue-500' : 'border-gray-200'}`}>
                                    <input type="radio" name="appearance.theme" value="system" checked={chatbot.appearance.theme === 'system'} onChange={handleInputChange} className="sr-only" />
                                    <span>System</span>
                                </label>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Primärfarbe</label>
                            <div className="mt-2 flex items-center">
                                <input
                                    type="color"
                                    name="appearance.primaryColor"
                                    value={chatbot.appearance.primaryColor}
                                    onChange={handleInputChange}
                                    className="h-8 w-8 border border-gray-300 rounded-md"
                                />
                                <input
                                    type="text"
                                    name="appearance.primaryColor"
                                    value={chatbot.appearance.primaryColor}
                                    onChange={handleInputChange}
                                    className="ml-2 border border-gray-300 rounded-md shadow-sm py-1 px-2"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Position auf der Webseite</label>
                            <div className="mt-2 grid grid-cols-2 gap-3">
                                <label className={`border rounded-md p-3 flex items-center justify-center cursor-pointer ${chatbot.appearance.position === 'right' ? 'bg-blue-50 border-blue-500' : 'border-gray-200'}`}>
                                    <input type="radio" name="appearance.position" value="right" checked={chatbot.appearance.position === 'right'} onChange={handleInputChange} className="sr-only" />
                                    <span>Rechts unten</span>
                                </label>
                                <label className={`border rounded-md p-3 flex items-center justify-center cursor-pointer ${chatbot.appearance.position === 'left' ? 'bg-blue-50 border-blue-500' : 'border-gray-200'}`}>
                                    <input type="radio" name="appearance.position" value="left" checked={chatbot.appearance.position === 'left'} onChange={handleInputChange} className="sr-only" />
                                    <span>Links unten</span>
                                </label>
                            </div>
                        </div>

                        <div className="bg-gray-50 p-6 rounded-md">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Vorschau</h3>
                            <div className="relative h-64 border border-gray-300 rounded-md overflow-hidden">
                                <div className="absolute right-0 bottom-0 m-4">
                                    <div className={`flex flex-col items-end`}>
                                        <button
                                            className={`w-12 h-12 rounded-full mb-2 flex items-center justify-center text-white shadow-lg`}
                                            style={{ backgroundColor: chatbot.appearance.primaryColor }}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'deploy' && (
                    <div>
                        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-blue-700">
                                        Dein Chatbot ist bereit für die Bereitstellung. Füge den folgenden Code zu deiner Website hinzu, um den Chatbot zu aktivieren.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Einbettungscode</label>
                            <div className="relative">
                <pre className="bg-gray-800 text-white rounded-md p-4 overflow-x-auto text-xs">
                  {`<script>
  (function(d, s, id) {
    var js, cjs = d.getElementsByTagName(s)[0];
    if (d.getElementById(id)) return;
    js = d.createElement(s); js.id = id;
    js.src = "https://app.chatbotbuilder.example.com/chatbot/${chatbot.id}.js";
    cjs.parentNode.insertBefore(js, cjs);
  }(document, 'script', 'chatbot-builder-js'));
</script>`}
                </pre>
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(`<script>
  (function(d, s, id) {
    var js, cjs = d.getElementsByTagName(s)[0];
    if (d.getElementById(id)) return;
    js = d.createElement(s); js.id = id;
    js.src = "https://app.chatbotbuilder.example.com/chatbot/${chatbot.id}.js";
    cjs.parentNode.insertBefore(js, cjs);
  }(document, 'script', 'chatbot-builder-js'));
</script>`);
                                        alert('Code kopiert!');
                                    }}
                                    className="absolute top-2 right-2 p-1 bg-gray-700 text-gray-300 rounded hover:bg-gray-600"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                                        <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div className="border-t border-gray-200 pt-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Direkter Link</h3>
                            <p className="text-gray-600 mb-4">Benutzer können deinen Chatbot auch direkt über diesen Link erreichen:</p>
                            <div className="flex">
                                <input
                                    type="text"
                                    value={`https://app.chatbotbuilder.example.com/chat/${chatbot.id}`}
                                    readOnly
                                    className="flex-1 border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50"
                                />
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(`https://app.chatbotbuilder.example.com/chat/${chatbot.id}`);
                                        alert('Link kopiert!');
                                    }}
                                    className="ml-2 px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
                                >
                                    Kopieren
                                </button>
                            </div>
                        </div>

                        <div className="mt-8">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Analytics</h3>
                            <div className="bg-gray-50 p-4 rounded-md">
                                <p className="text-gray-600">
                                    Dein Chatbot ist noch nicht live. Nach der Aktivierung findest du hier Nutzungsstatistiken und Analysen.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatbotSettings;